import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Link,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export type CheckoutAccountMode = "register" | "login";

export interface CheckoutAccountValues {
  name: string;
  email: string;
  phone: string;
  password: string;
  agreeToTerms: boolean;
}

export type CheckoutAccountErrors = Partial<Record<keyof CheckoutAccountValues, string>>;

export const EMPTY_CHECKOUT_ACCOUNT: CheckoutAccountValues = {
  name: "", email: "", phone: "", password: "", agreeToTerms: false,
};

/** Same rules the server enforces on sign-up — checked here first for instant feedback. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9\s\-()]{6,20}$/;
export const PASSWORD_MIN_LENGTH = 8;

export const validateCheckoutAccount = (
  v: CheckoutAccountValues,
  mode: CheckoutAccountMode
): CheckoutAccountErrors => {
  const errors: CheckoutAccountErrors = {};
  if (!EMAIL_PATTERN.test(v.email.trim())) errors.email = "Enter a valid email address.";
  if (mode === "login") {
    if (!v.password) errors.password = "Enter your password.";
    return errors;
  }
  if (v.name.trim().length < 2) errors.name = "Enter your full name.";
  if (!v.phone.trim()) errors.phone = "Enter your phone number.";
  else if (!PHONE_PATTERN.test(v.phone.trim())) errors.phone = "Enter a valid phone number, e.g. +961 70 123 456.";
  if (v.password.length < PASSWORD_MIN_LENGTH) {
    errors.password = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (!v.agreeToTerms) errors.agreeToTerms = "Please accept the terms to continue.";
  return errors;
};

interface Props {
  mode: CheckoutAccountMode;
  onModeChange: (mode: CheckoutAccountMode) => void;
  values: CheckoutAccountValues;
  onChange: (values: CheckoutAccountValues) => void;
  errors: CheckoutAccountErrors;
  /** Shown above the fields, e.g. "this email already has an account". */
  notice?: string;
  disabled?: boolean;
}

const BLUE = "#1565C0";
const NAVY = "#0A1628";
const MUTED = "#6B7280";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" } };

/**
 * Account details collected at checkout, so a visitor can buy without a
 * separate sign-up step. The account is created when they press pay — see
 * `PaymentRequest` — and an existing customer can log in here instead.
 */
const CheckoutAccountForm: React.FC<Props> = ({
  mode, onModeChange, values, onChange, errors, notice, disabled,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isLogin = mode === "login";

  const set = <K extends keyof CheckoutAccountValues>(key: K, value: CheckoutAccountValues[K]) =>
    onChange({ ...values, [key]: value });

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: "16px", border: "1.5px solid #E5E7EB", bgcolor: "#FAFBFF" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" sx={{ mb: 0.5, gap: 1 }}>
        <Typography variant="h6" fontWeight={800} sx={{ color: NAVY }}>
          {isLogin ? "Log in to your account" : "Your details"}
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: MUTED }}>
          {isLogin ? "New here? " : "Already have an account? "}
          <Link
            component="button"
            type="button"
            onClick={() => onModeChange(isLogin ? "register" : "login")}
            sx={{ fontWeight: 700, color: BLUE, verticalAlign: "baseline" }}
          >
            {isLogin ? "Create an account" : "Log in"}
          </Link>
        </Typography>
      </Stack>
      <Typography sx={{ color: MUTED, fontSize: "0.85rem", mb: 2 }}>
        {isLogin
          ? "Your order stays exactly as it is."
          : "We'll create your account when you pay, so you can open your leaflets right away."}
      </Typography>

      {notice && <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>{notice}</Alert>}

      <Stack spacing={2}>
        {!isLogin && (
          <TextField
            fullWidth label="Full name" name="name" autoComplete="name" required
            value={values.name} onChange={e => set("name", e.target.value)}
            error={!!errors.name} helperText={errors.name} disabled={disabled}
            inputProps={{ maxLength: 100 }} sx={fieldSx}
          />
        )}
        <TextField
          fullWidth label="Email" name="email" type="email" autoComplete="email" required
          value={values.email} onChange={e => set("email", e.target.value)}
          error={!!errors.email} helperText={errors.email ?? (isLogin ? undefined : "Your receipt and login go here.")}
          disabled={disabled}
          inputProps={{ maxLength: 255, inputMode: "email", autoCapitalize: "none", spellCheck: false }}
          sx={fieldSx}
        />
        {!isLogin && (
          <TextField
            fullWidth label="Phone number" name="phone" type="tel" autoComplete="tel" required
            placeholder="+961 70 123 456"
            value={values.phone} onChange={e => set("phone", e.target.value)}
            error={!!errors.phone} helperText={errors.phone} disabled={disabled}
            inputProps={{ maxLength: 20, inputMode: "tel" }} sx={fieldSx}
          />
        )}
        <TextField
          fullWidth label={isLogin ? "Password" : "Create a password"} name="password" required
          type={showPassword ? "text" : "password"}
          autoComplete={isLogin ? "current-password" : "new-password"}
          value={values.password} onChange={e => set("password", e.target.value)}
          error={!!errors.password}
          helperText={errors.password ?? (isLogin ? undefined : `At least ${PASSWORD_MIN_LENGTH} characters.`)}
          disabled={disabled}
          inputProps={{ maxLength: 72 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small" edge="end"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(s => !s)}
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        {isLogin ? (
          <Link
            component={RouterLink} to="/forgot-password" target="_blank" rel="noopener"
            sx={{ fontSize: "0.85rem", fontWeight: 600, color: BLUE, alignSelf: "flex-start" }}
          >
            Forgot your password?
          </Link>
        ) : (
          <Box>
            <FormControlLabel
              sx={{ alignItems: "flex-start", m: 0 }}
              control={
                <Checkbox
                  size="small" checked={values.agreeToTerms} disabled={disabled}
                  onChange={e => set("agreeToTerms", e.target.checked)}
                  sx={{ pt: 0.25, color: errors.agreeToTerms ? "#EF4444" : undefined, "&.Mui-checked": { color: BLUE } }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.85rem", color: NAVY }}>
                  I agree to the{" "}
                  <Link component={RouterLink} to="/terms" target="_blank" rel="noopener" sx={{ fontWeight: 700, color: BLUE }}>
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener" sx={{ fontWeight: 700, color: BLUE }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {errors.agreeToTerms && (
              <Typography sx={{ color: "#EF4444", fontSize: "0.75rem", mt: 0.5, ml: 3.5 }}>
                {errors.agreeToTerms}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default CheckoutAccountForm;
